import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef, memo, useCallback } from "react";
import HTMLFlipBook from "react-pageflip";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf";

GlobalWorkerOptions.workerSrc =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";

export const FlipBook = memo(
  forwardRef(function FlipBook(
    { pdfUrl, usePortrait = false, totalPages = 8, onPageChange, onPinch, onPdfError, onTotalPagesChange, onPdfReady },
    ref
  ) {
    const [page, setPage] = useState(0);
    const [pages, setPages] = useState([]);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);
    const [usePortraitState, setUsePortrait] = useState(false);
    const readyRef = useRef(false);
    
    const bookRef = useRef(null);
    const initialPinchDistance = useRef(null);
    const pinchHandled = useRef(false);
    const pinchActive = useRef(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const clamp = (n) => Math.max(0, Math.min(n, pages.length - 1));

    useImperativeHandle(ref, () => ({
      nextPage: () => {
        const inst = bookRef.current?.pageFlip?.();
        if (inst && inst.getState() !== "flipping") inst.flipNext();
      },
      prevPage: () => {
        const inst = bookRef.current?.pageFlip?.();
        if (inst && inst.getState() !== "flipping") inst.flipPrev();
      },
      goToPage: (n) => {
        const idx = clamp(n);
        const inst = bookRef.current?.pageFlip?.();
        if (inst) {
          if (typeof inst.turnToPage === "function") {
            inst.turnToPage(idx);
          } else if (typeof inst.flip === "function") {
            // fallback to animated flip
            if (inst.getState && inst.getState() === "flipping") return;
            inst.flip(idx);
          }
        } else {
          // older fallback if pageFlip() wrapper isn't present
          const instDirect = bookRef.current;
          if (instDirect?.flip) instDirect.flip(idx);
        }
        setPage(idx);
      },
      getCurrentPage: () => page,
      pageFlip: () => bookRef.current?.pageFlip?.(),
    }), [clamp, page]);

    useEffect(() => {
      if (onPageChange) onPageChange(page + 1);
    }, [onPageChange, page]);

    useEffect(() => {
      const updateLayout = () => {
        setUsePortrait(window.innerWidth < 600 && window.innerHeight > window.innerWidth);
      };
      updateLayout();
      window.addEventListener("resize", updateLayout);
      return () => window.removeEventListener("resize", updateLayout);
    }, []);

    const signalReadyOnce = useCallback(() => {
      if (readyRef.current) return;
      readyRef.current = true;
      if (onPdfReady) onPdfReady();
    }, [onPdfReady]);

    useEffect(() => {
      if (!pdfUrl) return;
      let cancelled = false;

      const loadPdf = async () => {
        try {
          // 1. Single Fetch: This replaces both the HEAD check and the GET request
          const res = await fetch(pdfUrl);

          // 2. Error Handling: Check if the file actually exists (prevents 404 errors)
          if (!res.ok) {
            console.error(`PDF not found: ${res.status}`);
            if (!cancelled && onPdfError) onPdfError(true); 
            return;
          }

          const buffer = await res.arrayBuffer();
          
          // 3. Prevent re-initialization if the component unmounted during fetch
          if (cancelled) return;

          const pdf = await getDocument({ data: buffer }).promise;
          
          if (!cancelled) {
            setPdfDoc(pdf);
            setPages(Array.from({ length: pdf.numPages }, (_, i) => i + 1));
            setLoading(false);
            if (onPdfError) onPdfError(false); // Reset error state if successful
            if (onTotalPagesChange) onTotalPagesChange(pdf.numPages);
            signalReadyOnce();
          }
        } catch (err) {
          if (!cancelled) {
            console.error("PDF load error", err);
            if (onPdfError) onPdfError(true);
          }
        }
      };

      loadPdf();
      
      // Clean up to prevent memory leaks and "Aborted" requests on unmount
      return () => { 
        cancelled = true; 
      };
    }, [pdfUrl, onPdfError, onTotalPagesChange, signalReadyOnce]); // Add onPdfError to dependencies



    useEffect(() => {
      if (!loading && bookRef.current) {
        const timer = setTimeout(() => {
          const inst = bookRef.current.pageFlip();
          if (inst) {
            inst.update(); 
            setIsReady(true); 
            signalReadyOnce();
          }
        }, 300); 
        return () => clearTimeout(timer);
      }
    }, [loading, signalReadyOnce]);

    const getDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    const onTouchStart = (e) => {
      if (e.touches?.length === 2) {
        initialPinchDistance.current = getDistance(e.touches[0], e.touches[1]);
        pinchActive.current = true;
      }
    };
    const onTouchMove = (e) => {
      if (!pinchActive.current || !initialPinchDistance.current) return;
      if (e.touches?.length === 2) {
        const cur = getDistance(e.touches[0], e.touches[1]);
        const scale = cur / initialPinchDistance.current;
        if (!pinchHandled.current && onPinch && (scale > 1.05 || scale < 0.95)) {
          pinchHandled.current = true;
          onPinch(scale);
        }
      }
    };

    return (
      <div 
        className={`flipbook-container ${isReady ? "is-ready" : "is-preparing"}`}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} 
        onTouchEnd={() => { pinchActive.current = false; pinchHandled.current = false; }}
      >
        {!loading && pdfDoc && (
          <HTMLFlipBook
            ref={bookRef}
            width={800} height={1000}
            size="stretch" minWidth={320} maxWidth={1400} minHeight={480}
            showCover mobileScrollSupport usePortrait={usePortraitState}
            flippingTime={1000}
            className="flipbook"
            onFlip={(e) => setPage(e.data)}
            drawShadow={true}           // Enable the flipping shadow
            maxShadowOpacity={0.5}     // Adjust for darkness
            showPageCorners={true}     // Adds a slight hover effect on corners
          >
            {pages.map((pageNumber) => (
              <div key={pageNumber} className="page">
                <div className="page-content">
                  <PageCanvas pdfDoc={pdfDoc} pageNumber={pageNumber} />
                </div>
              </div>
            ))}
          </HTMLFlipBook>
        )}

        {isReady && (
          <>
            <button className="flip-arrow left" onClick={() => ref.current?.prevPage()}>
              <svg className="flip-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="m15 19-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="flip-arrow right" onClick={() => ref.current?.nextPage()}>
              <svg className="flip-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="m9 5 7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  })
);

function PageCanvas({ pdfDoc, pageNumber }) {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const renderIdRef = useRef(0);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    // cancel any in-flight render on this canvas
    if (renderTaskRef.current?.cancel) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    const renderId = ++renderIdRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let cancelled = false;

    const renderPage = async () => {
      const page = await pdfDoc.getPage(pageNumber);
      if (cancelled || renderId !== renderIdRef.current) return;

      const viewport = page.getViewport({ scale: 1.5 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;

      await task.promise;
      if (renderId !== renderIdRef.current) return;
    };

    renderPage().catch((err) => {
      if (!cancelled && err?.name !== "RenderingCancelledException") {
        console.error("Page render error", err);
      }
    });

    return () => {
      cancelled = true;
      if (renderTaskRef.current?.cancel) renderTaskRef.current.cancel();
    };
  }, [pdfDoc, pageNumber]);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />;
}